import { store } from 'redux/store';
import { useCallback, useEffect, useState } from 'react';

import { Partner } from 'types';

import { getPartnersByFieldsAndQuery } from 'modules/partners/api/getPartnersByFieldsAndQuery';
import { CustomAsyncSelectOption } from 'modules/_shared/components/CustomAsyncSelect/types/CustomAsyncSelectOption';
import { getPartner } from '../redux/partnerActions';

type UseGetPartnerOptionsProps = {
  societyId?: string;
  partnerId?: string;
};

const FIELDS = 'cif name email image sharesCount address';

const mapOption = (partner: Partner): CustomAsyncSelectOption => ({
  value: partner?._id,
  label: `${partner?.cif} | ${partner?.name}`,
  name: partner?.name,
  cif: partner?.cif,
  email: partner?.email,
  image: partner?.image,
  sharesCount: partner?.sharesCount,
  address: partner?.address,
});

const mapOptions = (partners: Partner[]): CustomAsyncSelectOption[] =>
  partners.map((partner: Partner) => mapOption(partner));

export const useGetPartnerOptions = ({
  societyId,
  partnerId,
}: UseGetPartnerOptionsProps) => {
  const [selectedPartnerOption, setSelectedPartnerOption] =
    useState<CustomAsyncSelectOption | null>(null);
  const [partnerOptions, setPartnerOptions] = useState<
    CustomAsyncSelectOption[]
  >([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  const handleChangePartnerOption = (
    selectedOption: CustomAsyncSelectOption | null
  ) => {
    setSelectedPartnerOption(selectedOption);
  };

  const fetchPartners = useCallback(async () => {
    if (!societyId) return;
    setIsLoading(true);
    try {
      if (partnerId) {
        const partner = await store.dispatch(getPartner(partnerId));

        const selectedOption: CustomAsyncSelectOption = mapOption(partner);

        setSelectedPartnerOption(selectedOption);
        setIsLoading(false);
        return;
      }
      const partners = await store.dispatch(
        getPartnersByFieldsAndQuery(societyId, { fields: FIELDS })
      );

      const partnerOptions = mapOptions(partners);

      setPartnerOptions(partnerOptions);

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      setIsError(true);
    }
  }, [partnerId, societyId]);

  const handleSearchPartnerByTerm = async (searchTerm: string) => {
    if (!searchTerm || !societyId) return;
    try {
      setIsLoading(true);

      const partners = await store.dispatch(
        getPartnersByFieldsAndQuery(societyId, { fields: FIELDS, searchTerm })
      );

      const partnerOptions = mapOptions(partners);
      return partnerOptions;
    } catch (error) {
      setIsLoading(false);
      setIsError(true);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  return {
    data: {
      selectedPartnerOption,
      partnerOptions,
    },
    isLoading,
    isError,
    actions: {
      handleChangePartnerOption,
      handleSearchPartnerByTerm,
    },
  };
};
